import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Building2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "@/hooks/useSEO";
import { useContent } from "@/context/ContentContext";
import { api } from "@/lib/api";

const SIZES = ["Under 50.000 kr.", "50.000 – 250.000 kr.", "250.000 – 1 mio. kr.", "Over 1 mio. kr.", "Ved ikke endnu"];

const empty = {
  virksomhedsnavn: "", kontaktperson: "", cvr: "", telefon: "", email: "",
  projektadresse: "", startdato: "", projektstoerrelse: "", beskrivelse: "", website: "",
};

const Contact = () => {
  useSEO({
    title: "Kontakt | AA+PLUS MILJØSANERING – Indhent tilbud",
    description: "Kontakt AA+PLUS MILJØSANERING i Randers. Send en forespørgsel om konstruktionsarbejde, nedrivning, renovering eller byggepladsservice.",
  });
  const { content } = useContent();
  const c = content?.contact || {};
  const [form, setForm] = useState(empty);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const er = {};
    if (!form.virksomhedsnavn.trim()) er.virksomhedsnavn = "Angiv virksomhedsnavn";
    if (!form.kontaktperson.trim()) er.kontaktperson = "Angiv kontaktperson";
    if (!form.telefon.trim()) er.telefon = "Angiv telefonnummer";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Angiv en gyldig e-mail";
    if (!form.beskrivelse.trim() || form.beskrivelse.trim().length < 10) er.beskrivelse = "Beskriv opgaven (mindst 10 tegn)";
    if (!consent) er.consent = "Du skal acceptere privatlivspolitikken";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/inquiries", { ...form, samtykke: consent });
      setSent(true);
      setForm(empty);
      setConsent(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Der opstod en fejl. Prøv igen, eller ring til os.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (k) =>
    `w-full bg-white border rounded-sm px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF5A00] ${errors[k] ? "border-red-500" : "border-gray-300"}`;

  const phoneClean = (c.phone || "").replace(/\s/g, "");
  const mapsQuery = encodeURIComponent(`${c.address}, ${c.postal_city}, ${c.country}`);

  return (
    <div data-testid="contact-page">
      <section className="bg-[#1F2937] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-[#FF5A00] font-semibold text-sm uppercase tracking-widest">Kontakt</span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight text-white max-w-3xl">Indhent tilbud eller kontakt os</h1>
          <p className="mt-5 text-lg text-gray-300 max-w-2xl leading-relaxed">Send en kort beskrivelse af opgaven. Vi kontakter jer for at gennemgå projektet og mulighederne for et samarbejde.</p>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          {/* Info */}
          <div className="space-y-8">
            <div className="bg-gray-50 border border-gray-200 rounded-sm p-7">
              <div className="flex items-center gap-3 mb-5"><Building2 className="text-[#FF5A00]" size={22} /><h2 className="font-bold text-gray-900">{c.company_name}</h2></div>
              <ul className="space-y-4 text-gray-700 text-sm">
                <li className="flex items-start gap-3"><MapPin size={18} className="text-[#FF5A00] mt-0.5 shrink-0" /><span>{c.address}<br />{c.postal_city}<br />CVR: {c.cvr}</span></li>
                <li className="flex items-start gap-3"><Phone size={18} className="text-[#FF5A00] mt-0.5 shrink-0" /><a href={`tel:${phoneClean}`} data-testid="contact-phone-link" className="hover:text-[#FF5A00]">{c.phone}</a></li>
                <li className="flex items-start gap-3"><Mail size={18} className="text-[#FF5A00] mt-0.5 shrink-0" /><a href={`mailto:${c.email}`} data-testid="contact-email-link" className="hover:text-[#FF5A00] break-all">{c.email}</a></li>
                <li className="flex items-start gap-3"><Clock size={18} className="text-[#FF5A00] mt-0.5 shrink-0" /><span>{c.hours}</span></li>
              </ul>
            </div>
            <div className="overflow-hidden rounded-sm border border-gray-200 h-64">
              <iframe
                title="Kort over adressen"
                data-testid="google-map"
                width="100%" height="100%" style={{ border: 0 }} loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${mapsQuery}&output=embed`}
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            {sent ? (
              <div data-testid="contact-success" className="bg-[#FF5A00]/5 border-2 border-[#FF5A00] rounded-sm p-10 text-center">
                <CheckCircle2 className="text-[#FF5A00] mx-auto mb-4" size={48} />
                <h2 className="text-2xl font-bold text-gray-900">Tak for din henvendelse.</h2>
                <p className="mt-3 text-gray-600">Vi kontakter dig hurtigst muligt.</p>
                <button onClick={() => setSent(false)} data-testid="send-another-btn" className="mt-6 text-[#FF5A00] font-semibold underline">Send en ny forespørgsel</button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-gray-200 rounded-sm p-7 lg:p-10 space-y-5" noValidate>
                <input type="text" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Virksomhedsnavn *" error={errors.virksomhedsnavn}><input data-testid="input-virksomhedsnavn" className={inputCls("virksomhedsnavn")} value={form.virksomhedsnavn} onChange={set("virksomhedsnavn")} /></Field>
                  <Field label="Kontaktperson *" error={errors.kontaktperson}><input data-testid="input-kontaktperson" className={inputCls("kontaktperson")} value={form.kontaktperson} onChange={set("kontaktperson")} /></Field>
                  <Field label="CVR-nummer" error={errors.cvr}><input data-testid="input-cvr" className={inputCls("cvr")} value={form.cvr} onChange={set("cvr")} /></Field>
                  <Field label="Telefon *" error={errors.telefon}><input data-testid="input-telefon" className={inputCls("telefon")} value={form.telefon} onChange={set("telefon")} /></Field>
                  <Field label="E-mail *" error={errors.email}><input data-testid="input-email" type="email" className={inputCls("email")} value={form.email} onChange={set("email")} /></Field>
                  <Field label="Projektadresse" error={errors.projektadresse}><input data-testid="input-projektadresse" className={inputCls("projektadresse")} value={form.projektadresse} onChange={set("projektadresse")} /></Field>
                  <Field label="Ønsket startdato" error={errors.startdato}><input data-testid="input-startdato" type="date" className={inputCls("startdato")} value={form.startdato} onChange={set("startdato")} /></Field>
                  <Field label="Estimeret projektstørrelse" error={errors.projektstoerrelse}>
                    <select data-testid="input-projektstoerrelse" className={inputCls("projektstoerrelse")} value={form.projektstoerrelse} onChange={set("projektstoerrelse")}>
                      <option value="">Vælg...</option>
                      {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Beskrivelse af opgaven *" error={errors.beskrivelse}>
                  <textarea data-testid="input-beskrivelse" rows={5} className={inputCls("beskrivelse")} value={form.beskrivelse} onChange={set("beskrivelse")} />
                </Field>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" data-testid="input-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-4 h-4 accent-[#FF5A00]" />
                  <span className="text-sm text-gray-600">Jeg accepterer, at mine oplysninger behandles i henhold til <a href="/privatlivspolitik" className="text-[#FF5A00] underline">privatlivspolitikken</a>. *</span>
                </label>
                {errors.consent && <p className="text-sm text-red-600 -mt-2">{errors.consent}</p>}
                <button type="submit" disabled={submitting} data-testid="submit-inquiry-btn" className="w-full sm:w-auto bg-[#FF5A00] text-white font-semibold px-8 py-4 rounded-sm hover:bg-[#E04F00] transition-colors disabled:opacity-60">
                  {submitting ? "Sender..." : "Send forespørgsel"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-800 mb-1.5">{label}</label>
    {children}
    {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
  </div>
);

export default Contact;
